import { Head, Link, router } from '@inertiajs/react'
import AppLayout from '../../layouts/AppLayout'
import { FileCheck, Clock, FileText, GraduationCap, CheckCircle, UserPlus } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'

interface Agent {
  id: number
  name: string
  email: string
  agent_type: string
  onboarding_status: string
  contract_signed_at: string | null
  activated_at: string | null
  created_at: string
  surancebay_completed_at: string | null
  ffr_registration_completed_at: string | null
}

function CompletionStatus({ completedAt }: { completedAt: string | null }) {
  if (completedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap bg-green-100 text-green-800">
        <CheckCircle className="h-4 w-4" />
        {new Date(completedAt).toLocaleDateString()}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap bg-gray-100 text-gray-600">
      <Clock className="h-4 w-4" />
      Not completed
    </span>
  )
}

interface Summary {
  total: number
  contract_pending: number
  documents_pending: number
  training_pending: number
  active: number
}

interface Props {
  pending_agents: Agent[]
  completed_agents: Agent[]
  summary: Summary
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  prospect: { label: 'Prospect', color: 'bg-gray-100 text-gray-800', icon: Clock },
  invited: { label: 'Invited', color: 'bg-blue-100 text-blue-800', icon: Clock },
  contract_pending: { label: 'Pending Contract', color: 'bg-amber-100 text-amber-800', icon: FileText },
  documents_pending: { label: 'Pending Documents', color: 'bg-blue-100 text-blue-800', icon: FileCheck },
  training_pending: { label: 'In Training', color: 'bg-purple-100 text-purple-800', icon: GraduationCap },
  active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-800', icon: Clock }
}

export default function OnboardingIndex({ pending_agents, completed_agents, summary }: Props) {
  const pendingColumns: Column<Agent>[] = [
    {
      key: 'name',
      header: 'Agent',
      sortable: true,
      render: (agent) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-chestnut-forest/10 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-chestnut-forest">
              {agent.name.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <Link href={`/agents/${agent.id}`} className="font-medium text-chestnut-charcoal hover:text-chestnut-forest">
              {agent.name}
            </Link>
            <p className="text-sm text-chestnut-charcoal/60">{agent.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'agent_type',
      header: 'Type',
      sortable: true,
      render: (agent) => (
        <span className="capitalize text-sm text-chestnut-charcoal/60">{agent.agent_type.replace('_', ' ')}</span>
      )
    },
    {
      key: 'onboarding_status',
      header: 'Status',
      sortable: true,
      render: (agent) => {
        const status = statusConfig[agent.onboarding_status] || statusConfig.contract_pending
        const StatusIcon = status.icon
        return (
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${status.color}`}>
            <StatusIcon className="h-4 w-4" />
            {status.label}
          </span>
        )
      }
    },
    {
      key: 'surancebay_completed_at',
      header: 'SuranceBay',
      sortable: true,
      render: (agent) => <CompletionStatus completedAt={agent.surancebay_completed_at} />
    },
    {
      key: 'ffr_registration_completed_at',
      header: 'FFR Registration',
      sortable: true,
      render: (agent) => <CompletionStatus completedAt={agent.ffr_registration_completed_at} />
    },
    {
      key: 'created_at',
      header: 'Started',
      sortable: true,
      render: (agent) => new Date(agent.created_at).toLocaleDateString()
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (agent) => (
        <Link href={`/agents/${agent.id}`} className="text-sm text-chestnut-forest hover:underline">
          View Details
        </Link>
      )
    }
  ]

  const completedColumns: Column<Agent>[] = [
    {
      key: 'name',
      header: 'Agent',
      sortable: true,
      render: (agent) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <Link href={`/agents/${agent.id}`} className="font-medium text-chestnut-charcoal hover:text-chestnut-forest">
              {agent.name}
            </Link>
            <p className="text-sm text-chestnut-charcoal/60">{agent.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'agent_type',
      header: 'Type',
      sortable: true,
      render: (agent) => (
        <span className="capitalize text-sm text-chestnut-charcoal/60">{agent.agent_type.replace('_', ' ')}</span>
      )
    },
    {
      key: 'surancebay_completed_at',
      header: 'SuranceBay',
      sortable: true,
      render: (agent) => <CompletionStatus completedAt={agent.surancebay_completed_at} />
    },
    {
      key: 'ffr_registration_completed_at',
      header: 'FFR Registration',
      sortable: true,
      render: (agent) => <CompletionStatus completedAt={agent.ffr_registration_completed_at} />
    },
    {
      key: 'activated_at',
      header: 'Completed',
      sortable: true,
      render: (agent) => agent.activated_at ? new Date(agent.activated_at).toLocaleDateString() : 'N/A'
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (agent) => (
        <Link href={`/agents/${agent.id}`} className="text-sm text-chestnut-forest hover:underline">
          View Profile
        </Link>
      )
    }
  ]

  return (
    <AppLayout>
      <Head title="Agent Onboarding" />
      
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-display font-bold text-chestnut-charcoal">Agent Onboarding</h1>
            <p className="text-chestnut-charcoal/60 mt-1">Track and manage agent onboarding progress</p>
          </div>
          <Link href="/agents/new" className="btn-primary flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Add New Agent
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-chestnut-charcoal/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{summary.contract_pending}</p>
                <p className="text-sm text-chestnut-charcoal/60">Pending Contract</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-chestnut-charcoal/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{summary.documents_pending}</p>
                <p className="text-sm text-chestnut-charcoal/60">Pending Docs</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-chestnut-charcoal/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <GraduationCap className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{summary.training_pending}</p>
                <p className="text-sm text-chestnut-charcoal/60">In Training</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-chestnut-charcoal/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{summary.active}</p>
                <p className="text-sm text-chestnut-charcoal/60">Active</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="p-6 border-b border-chestnut-charcoal/10">
            <h2 className="text-lg font-semibold text-chestnut-charcoal">Agents In Progress</h2>
          </div>
          <div className="p-6">
            <DataTable
              data={pending_agents}
              columns={pendingColumns}
              searchPlaceholder="Search agents..."
              searchKeys={['name', 'email']}
              pageSize={10}
              emptyMessage="All agents have completed onboarding!"
              getRowKey={(agent) => agent.id}
              onRowClick={(agent) => router.visit(`/agents/${agent.id}`)}
            />
          </div>
        </div>

        {completed_agents.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
            <div className="p-6 border-b border-chestnut-charcoal/10">
              <h2 className="text-lg font-semibold text-chestnut-charcoal">Recently Completed</h2>
            </div>
            <div className="p-6">
              <DataTable
                data={completed_agents}
                columns={completedColumns}
                searchPlaceholder="Search completed agents..."
                searchKeys={['name', 'email']}
                pageSize={5}
                emptyMessage="No completed agents yet."
                getRowKey={(agent) => agent.id}
                onRowClick={(agent) => router.visit(`/agents/${agent.id}`)}
              />
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
